import React, { useCallback, useState } from 'react';
import { debounce, get } from 'lodash';
import { Autocomplete, Box, CircularProgress, TextField, ListSubheader } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useGetClassificationCodesHandler } from '../../resources/products/dataHandlers/getClassificationCodesHandler';

const ClassificationCodeSelect = ({ label, name, control, errors, helperText, readOnly = false }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const modifySearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value.trim());
    }, 500),
    [],
  );

  const { codes, isLoading } = useGetClassificationCodesHandler({
    searchQuery: {
      page: 0,
      size: 10,
      code: searchTerm || null,
    },
  });

  const errorMessage = get(errors, name, {})?.message;
  const combinedHelperText = [helperText, errorMessage].filter(Boolean).join(' * ');

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const selectedValue = (() => {
          if (!field.value) return null;

          if (typeof field.value === 'string') {
            return (
              (codes || []).find((opt) => opt.code === field.value) || {
                code: field.value,
                description: '',
              }
            );
          }

          if (field.value.description) return field.value;

          return (codes || []).find((opt) => opt.code === field.value.code) || field.value;
        })();
        return (
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Autocomplete
              freeSolo
              loading={isLoading}
              id={name}
              fullWidth
              readOnly={readOnly}
              options={codes || []}
              groupBy={(option) => option.group}
              renderGroup={(params) => (
                <li key={params.key}>
                  <ListSubheader
                    sx={{
                      minHeight: '32px',
                      color: '#111111',
                      fontWeight: 600,
                      lineHeight: '24px',
                      paddingLeft: '5px',
                    }}
                  >
                    {params.group}
                  </ListSubheader>
                  {params.children}
                </li>
              )}
              getOptionLabel={(option) => {
                if (!option) return '';
                if (typeof option === 'string') return option;
                if (!option.description) return option.code || '';
                return `${option.code} - ${option.description}`;
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Box key={key} component="li" {...optionProps}>
                    {option.code} - {option.description}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  error={!!errorMessage}
                  helperText={combinedHelperText}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              value={selectedValue}
              onChange={(_, value) => {
                let finalValue = null;
                if (value === null) {
                  finalValue = null;
                } else if (typeof value === 'string') {
                  finalValue = { code: value.trim(), description: '' };
                } else {
                  finalValue = value;
                }
                field.onChange(finalValue);
              }}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  modifySearchTerm(value);
                  field.onChange(value ? { code: value } : null);
                }
              }}
              isOptionEqualToValue={(option, value) => option.code === value?.code}
              filterOptions={(x) => x}
            />
          </Box>
        );
      }}
    />
  );
};

export default ClassificationCodeSelect;
